/**
 * Утилиты для валидации данных отчётов и логирования
 */

// Импорт moment для типизации (только типы, без runtime)
import type { Moment } from 'moment';

// ==================== ЛОГИРОВАНИЕ ====================

const isDev = process.env.NODE_ENV === 'development';

/**
 * Логирование только в dev-режиме
 */
export const devLog = (message: string, ...args: unknown[]): void => {
    if (isDev) {
        console.log(message, ...args);
    }
};

/**
 * Предупреждение только в dev-режиме
 */
export const devWarn = (message: string, ...args: unknown[]): void => {
    if (isDev) {
        console.warn(message, ...args);
    }
};

/**
 * Ошибка только в dev-режиме
 */
export const devError = (message: string, ...args: unknown[]): void => {
    if (isDev) {
        console.error(message, ...args);
    }
};

// ==================== ВАЛИДАЦИЯ ====================

/**
 * Интерфейс периода для валидации.
 * Совместим с IPeriodState из Redux (start/end могут быть Moment или string).
 */
export interface Period {
    start?: string | Moment | null;
    end?: string | Moment | null;
}

export interface GroupingItem {
    dataKey: string;
    name?: string;
    label?: string;
    [key: string]: unknown;
}

export interface MetricItem {
    dataKey: string;
    name?: string;
    label?: string;
    [key: string]: unknown;
}

export interface ValidationResult {
    isValid: boolean;
    reason?: string;
}

/**
 * Проверяет валидность группировок
 */
export const validateGroupings = (groupings: GroupingItem[] | null | undefined): ValidationResult => {
    if (!groupings || !Array.isArray(groupings) || groupings.length === 0) {
        return { isValid: false, reason: 'groupings is empty or invalid' };
    }
    return { isValid: true };
};

/**
 * Проверяет валидность метрик
 */
export const validateMetrics = (metrics: MetricItem[] | null | undefined): ValidationResult => {
    if (!metrics || !Array.isArray(metrics) || metrics.length === 0) {
        return { isValid: false, reason: 'metrics is empty or invalid' };
    }
    return { isValid: true };
};

/**
 * Проверяет валидность периода
 */
export const validatePeriod = (period: Period | null | undefined): ValidationResult => {
    if (!period || !period.start || !period.end) {
        return { isValid: false, reason: 'period is invalid or incomplete' };
    }
    return { isValid: true };
};

/**
 * Комплексная проверка данных для запроса отчёта
 */
export const validateReportRequestData = (data: {
    groupings?: GroupingItem[] | null;
    metrics?: MetricItem[] | null;
    period?: Period | null;
}): ValidationResult => {
    const groupingsValidation = validateGroupings(data.groupings);
    if (!groupingsValidation.isValid) {
        return groupingsValidation;
    }

    const metricsValidation = validateMetrics(data.metrics);
    if (!metricsValidation.isValid) {
        return metricsValidation;
    }

    const periodValidation = validatePeriod(data.period);
    if (!periodValidation.isValid) {
        return periodValidation;
    }

    return { isValid: true };
};

/**
 * Проверяет и логирует результат валидации для таблицы
 * @returns true если данные валидны, false если нет
 */
export const validateTableRequest = (
    groupings: GroupingItem[] | null | undefined,
    metrics: MetricItem[] | null | undefined,
    period: Period | null | undefined,
    componentName = 'ReportTable'
): boolean => {
    const validation = validateReportRequestData({ groupings, metrics, period });
    
    if (!validation.isValid) {
        devLog(`[${componentName}] Request blocked: ${validation.reason}`);
        return false;
    }
    
    return true;
};

/**
 * Проверяет и логирует результат валидации для графика
 * @returns true если данные валидны, false если нет
 */
export const validateChartRequest = (
    groupings: GroupingItem[] | null | undefined,
    period: Period | null | undefined,
    componentName = 'ReportChart'
): boolean => {
    const groupingsValidation = validateGroupings(groupings);
    if (!groupingsValidation.isValid) {
        devLog(`[${componentName}] Request blocked: ${groupingsValidation.reason}`);
        return false;
    }

    const periodValidation = validatePeriod(period);
    if (!periodValidation.isValid) {
        devLog(`[${componentName}] Request blocked: ${periodValidation.reason}`);
        return false;
    }
    
    return true;
};
